import {rootState} from '@mintdata-wsp/common/state/RootState';
import {CopiedCellOffsetsKey, CopyPasteState} from '@mintdata-wsp/common/state/ui/visualDesigner/CopyPasteState';
import {
    DefaultMeshCellSettings,
    MeshCellColor,
    MeshCellSettings
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshSheetSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSheetSize';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {
    CellSettingsBarBorderAction,
    CellSettingsBarMenu,
    CellSettingsBarState,
    CellSettingsBarTmpValue
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/CellSettingsBarState';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {action} from 'mobx';

export class CellSettingsBarController
{
    @action
    private _setState<T extends keyof CellSettingsBarState>(prop:T, value:CellSettingsBarState[T]):void
    {
        const {cellSettingsBar} = rootState.ui.visualDesigner.workbench;

        cellSettingsBar[prop] = value;
    }

    @action
    private _toggleState<T extends keyof CellSettingsBarState>(prop:T):void
    {
        const {cellSettingsBar} = rootState.ui.visualDesigner.workbench;

        cellSettingsBar[prop] = !cellSettingsBar[prop];
    }

    public toggleBarVisibility():void
    {
        this._toggleState('isBarVisible');
    }

    public setBarVisibility(visibility:boolean):void
    {
        this._setState('isBarVisible', visibility);
    }

    // @action
    // public getProperty<T extends keyof CellSettingsBarState>(prop:T):T
    // {
    //     const {cellSettingsBar} = rootState.ui.visualDesigner.workbench;
    //
    //     return cellSettingsBar[prop];
    // }

    // @action
    // public setProperty<T extends keyof CellSettingsBarState>(prop:T, value:CellSettingsBarState[T]):void
    // {
    //     const {cellSettingsBar} = rootState.ui.visualDesigner.workbench;
    //
    //     cellSettingsBar[prop] = value;
    // }

    // @action
    // public toggleProperty<T extends keyof CellSettingsBarState>(prop:T):void
    // {
    //     const {cellSettingsBar} = rootState.ui.visualDesigner.workbench;
    //
    //     cellSettingsBar[prop] = !cellSettingsBar[prop];
    // }

    public setIsMenuOpened(cellSettingsBarMenu:CellSettingsBarMenu)
    {
        this._setState('isMenuOpened', cellSettingsBarMenu);

        // if( cellSettingsBarMenu == CellSettingsBarMenu.Border && !isOpened )
        if( cellSettingsBarMenu == CellSettingsBarMenu.Border )
            this.setSelectedBorderAction(null);
    }

    public setTmpValue(tmpValue:CellSettingsBarTmpValue)
    {
        this._setState('tmpValue', tmpValue);
    }

    public setSelectedBorderAction(action:CellSettingsBarBorderAction):void
    {
        this._setState('selectedBorderAction', action);
    }

    public setBorderColor(color:MeshCellColor):void
    {
        this._setState('borderColor', color || DefaultMeshCellSettings.borderColor);

        const {selectedBorderAction} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        if( selectedBorderAction )
        {
            controllers.ui.spreadsheets.meshCellSettings.setBorder(this.getFocusedMeshCtx(), selectedBorderAction);
        }
    }

    public toggleBorderType():void
    {
        this._toggleState('isBorderTypeDouble');

        const {selectedBorderAction} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        if( selectedBorderAction )
        {
            controllers.ui.spreadsheets.meshCellSettings.setBorder(this.getFocusedMeshCtx(), selectedBorderAction);
        }
    }

    @action
    public togglePaintFormatMode(value?:boolean):void
    {
        const {cellSettingsBar} = rootState.ui.visualDesigner.workbench;

        cellSettingsBar.isPaintFormatMode = value !== undefined
            ? value
            : !cellSettingsBar.isPaintFormatMode;

        const {isPaintFormatMode} = cellSettingsBar;

        if( isPaintFormatMode )
        {
            const meshState = this.getFocusedMeshState();

            const {details: {selectedRange}} = meshState;

            // -----------------------------------------------------------------
            let from = selectedRange.from;
            let to = selectedRange.to;

            if( from.colIndex === null )
            {
                from = new MeshLocation(0, from.rowIndex);
                to = new MeshLocation(meshState.size.numCols - 1, to.rowIndex);
            }

            if( from.rowIndex === null )
            {
                from = new MeshLocation(from.colIndex, 0);
                to = new MeshLocation(to.colIndex, meshState.size.numRows - 1);
            }
            // -----------------------------------------------------------------

            this._setState('paintFormatData',
                {
                    copiedSheetId: this.getFocusedMeshCtx().sheetId,
                    copiedRange: new MeshRange(from, to || from)
                });
        }
        else
        {
            this._setState('paintFormatData', null);
        }
    }

    @action
    public applyPaintFormatCellSetting(ctx:IWSPMeshContext):void
    {
        const {sheetSettingsCtrl} = ctx;
        const {cellSettingsBar} = rootState.ui.visualDesigner.workbench;

        this._setState('isPaintFormatMode', false);

        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);
        if( !meshState.details.selectedRange ) return;

        const {copiedSheetId, copiedRange} = cellSettingsBar.paintFormatData;

        // -----------------------------------------------------------------
        let copiedCellSettings = new Map<CopiedCellOffsetsKey, MeshCellSettings>();
        let copiedCellFormats = new Map<CopiedCellOffsetsKey, string>();

        let copiedFrom = copiedRange.from;
        let copiedTo = copiedRange.to;

        for( let rowIndex = copiedFrom.rowIndex; rowIndex <= (copiedTo || copiedFrom).rowIndex; rowIndex++ )
        {
            for( let colIndex = copiedFrom.colIndex; colIndex <= (copiedTo || copiedFrom).colIndex; colIndex++ )
            {
                const colOffset = colIndex - copiedFrom.colIndex;
                const rowOffset = rowIndex - copiedFrom.rowIndex;

                const meshLocationKey = new MeshLocation(colIndex, rowIndex).asKey();
                let cellSettings = sheetSettingsCtrl.getCellSettings(
                    copiedSheetId,
                    meshLocationKey
                );

                const key = CopyPasteState.makeCopiedCellOffsetsKey(colOffset, rowOffset);
                copiedCellSettings.set(key, cellSettings);
                copiedCellFormats.set(key, sheetSettingsCtrl.getCellFormat(copiedSheetId, meshLocationKey).id);
            }
        }
        // -----------------------------------------------------------------

        const {details: {selectedRange}} = meshState;

        let from = selectedRange.from;
        let to = selectedRange.to;

        if( from.colIndex === null )
        {
            from = new MeshLocation(0, from.rowIndex);
            to = new MeshLocation(meshState.size.numCols - 1, to.rowIndex);
        }

        if( from.rowIndex === null )
        {
            from = new MeshLocation(from.colIndex, 0);
            to = new MeshLocation(to.colIndex, meshState.size.numRows - 1);
        }

        let pasteRange = new MeshRange(from, to || from);

        const {columnsCount, rowsCount} = pasteRange;

        const colsRepeatCount = Math.max(1, Math.floor(columnsCount / copiedRange.columnsCount));
        const rowsRepeatCount = Math.max(1, Math.floor(rowsCount / copiedRange.rowsCount));

        for( let colRepeatIndex = 0; colRepeatIndex < colsRepeatCount; colRepeatIndex++ )
        {
            for( let rowRepeatIndex = 0; rowRepeatIndex < rowsRepeatCount; rowRepeatIndex++ )
            {
                this._pasteCellSettings(
                    ctx,
                    from.colIndex + colRepeatIndex * copiedRange.columnsCount,
                    from.rowIndex + rowRepeatIndex * copiedRange.rowsCount,
                    copiedCellSettings,
                    copiedCellFormats,
                    copiedRange
                );
            }
        }

        controllers.ui.spreadsheets.meshCopyPaste.adjustSelectedRange(
            meshState,
            from.colIndex,
            from.rowIndex,
            colsRepeatCount * copiedRange.columnsCount,
            rowsRepeatCount * copiedRange.rowsCount
        );
    }

    public getFocusedMeshCtx():IWSPMeshContext
    {
        const {main, workbook} = controllers.ui.spreadsheets;

        const rootWorkbook = main.getRootWorkbook();

        return workbook.getMeshContextBySheetId(rootWorkbook.view.focusedSheetId);
    }

    public getFocusedMeshState():MeshState
    {
        const {mesh} = controllers.ui.spreadsheets;

        return mesh.getState(this.getFocusedMeshCtx());
    }

    @action
    private _pasteCellSettings(destCtx:IWSPMeshContext,
                               startColIndex:number,
                               startRowIndex:number,
                               copiedCellSettings:Map<CopiedCellOffsetsKey, MeshCellSettings>,
                               copiedCellFormats:Map<CopiedCellOffsetsKey, string>,
                               copiedRange:MeshRange):void
    {
        const {sheetSettingsCtrl, sheetId} = destCtx;

        const colsFromCopiedRangeCount = copiedRange.columnsCount;
        const rowsFromCopiedRangeCount = copiedRange.rowsCount;

        for( let colOffset = 0; colOffset < colsFromCopiedRangeCount; colOffset++ )
        {
            for( let rowOffset = 0; rowOffset < rowsFromCopiedRangeCount; rowOffset++ )
            {
                const destColIndex = startColIndex + colOffset;
                const destRowIndex = startRowIndex + rowOffset;
                const destLocation = new MeshLocation(destColIndex, destRowIndex);
                const key = CopyPasteState.makeCopiedCellOffsetsKey(colOffset, rowOffset);

                const cellSettings = copiedCellSettings.get(key);
                const cellFormatId = copiedCellFormats.get(key);

                sheetSettingsCtrl.setCellSettings(
                    sheetId,
                    destLocation.asKey(),
                    cellSettings ? cellSettings.clone() : null
                );
                sheetSettingsCtrl.setCellFormat(
                    sheetId,
                    new MeshRange(destLocation, destLocation),
                    MeshSheetSize.singleCell(),
                    cellFormatId
                );
            }
        }
    }
}
